import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { addDaysToDateKey, localDateKey } from '../lib/dateKey';
import { circleDayLabel, circleRecentDayKeys } from '../lib/circles/dateNav';
import {
  ActivityFeedItem,
  ChallengeCard,
  CircleProgressRing,
  LeaderboardRow,
  QuickActionButton,
  SharedMealCard,
  WorkoutPingCard,
} from '../components/circles/CircleCards';
import {
  BottomSheet,
  PremiumCard,
  PremiumPage,
  PrimaryButton,
  SectionHeader,
} from '../components/premium';
import { MemberDaySheet } from '../components/circles/MemberDaySheet';
import {
  fetchCircleDetail,
  fetchMemberDay,
  joinChallenge,
  NUDGE_TEMPLATES,
  respondToPing,
  sendNudge,
  sendWorkoutPing,
  shareMealToCircle,
  startChallenge,
  updateCirclePrivacy,
} from '../lib/circles/api';
import {
  AI_MEAL_ADJUSTMENTS,
  CHALLENGE_TYPES,
  type CircleDetail,
  type CirclePrivacySettings,
  type MemberDaySnapshot,
} from '../types/circles';
import { shareCircleInvite } from '../lib/circles/inviteLink';
import { fieldControl, btnSecondary, btnToolbarActive } from '../lib/uiClasses';

export function CircleDetailPage() {
  const { circleId = '' } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<CircleDetail | null>(null);
  const [memberSheetOpen, setMemberSheetOpen] = useState(false);
  const [memberSnapshot, setMemberSnapshot] = useState<MemberDaySnapshot | null>(null);
  const [memberLoading, setMemberLoading] = useState(false);

  const [pingOpen, setPingOpen] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [mealOpen, setMealOpen] = useState(false);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [aiMealOpen, setAiMealOpen] = useState(false);

  const [pingMessage, setPingMessage] = useState('');
  const [nudgeTarget, setNudgeTarget] = useState('');
  const [nudgeType, setNudgeType] = useState('comeback');
  const [customNudge, setCustomNudge] = useState('');
  const [mealTitle, setMealTitle] = useState('');
  const [mealCal, setMealCal] = useState('520');
  const [mealP, setMealP] = useState('48');
  const [mealC, setMealC] = useState('52');
  const [mealF, setMealF] = useState('14');
  const [mealPrep, setMealPrep] = useState('10');
  const [mealRecipe, setMealRecipe] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const todayKey = localDateKey();
  const [selectedDateKey, setSelectedDateKey] = useState(todayKey);

  const dayTabs = useMemo(() => circleRecentDayKeys(14, todayKey), [todayKey]);
  const dateLabel = circleDayLabel(selectedDateKey, todayKey);
  const isToday = selectedDateKey === todayKey;

  const load = useCallback(async () => {
    if (!circleId) return;
    setLoading(true);
    try {
      const d = await fetchCircleDetail(circleId, selectedDateKey);
      setDetail(d);
    } catch {
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [circleId, selectedDateKey]);

  async function openMember(userId: string) {
    setMemberSheetOpen(true);
    setMemberLoading(true);
    setMemberSnapshot(null);
    try {
      const snap = await fetchMemberDay(circleId, userId, selectedDateKey);
      setMemberSnapshot(snap);
    } catch {
      setMemberSnapshot(null);
    } finally {
      setMemberLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [load]);

  function showToast(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2800);
  }

  async function handleInviteFriends() {
    if (!detail) return;
    try {
      const result = await shareCircleInvite(detail.name, detail.inviteCode);
      showToast(result === 'shared' ? 'Invite sent!' : 'Invite link copied');
    } catch {
      showToast('Could not share invite');
    }
  }

  async function handlePing() {
    const message = pingMessage.trim();
    if (!message) return;
    setBusy(true);
    try {
      await sendWorkoutPing(circleId, { workoutType: 'Workout', message });
      setPingOpen(false);
      setPingMessage('');
      showToast('Workout invite sent');
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function handleNudge() {
    if (!nudgeTarget) return;
    setBusy(true);
    try {
      const tpl = NUDGE_TEMPLATES.find((t) => t.key === nudgeType);
      await sendNudge(circleId, nudgeTarget, nudgeType, customNudge || tpl?.message);
      setNudgeOpen(false);
      showToast('Positive nudge sent');
    } finally {
      setBusy(false);
    }
  }

  async function handleShareMeal() {
    if (!mealTitle.trim()) return;
    setBusy(true);
    try {
      await shareMealToCircle(circleId, {
        title: mealTitle.trim(),
        calories: Number(mealCal) || 0,
        proteinG: Number(mealP) || 0,
        carbsG: Number(mealC) || 0,
        fatG: Number(mealF) || 0,
        prepTime: Number(mealPrep) || undefined,
        recipe: mealRecipe.trim() || undefined,
      });
      setMealOpen(false);
      showToast('Meal shared with circle');
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function handleStartChallenge(type: (typeof CHALLENGE_TYPES)[number]) {
    setBusy(true);
    try {
      await startChallenge(circleId, {
        title: type.title,
        type: type.id,
        durationDays: type.days,
        goalType: type.type,
        target: type.days,
      });
      setChallengeOpen(false);
      showToast('Challenge started');
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function patchPrivacy(key: keyof CirclePrivacySettings, value: boolean) {
    if (!detail) return;
    const prev = detail.privacy;
    const next = { ...prev, [key]: value };
    setDetail({ ...detail, privacy: next });
    try {
      await updateCirclePrivacy(circleId, { [key]: value });
    } catch {
      setDetail((d) => (d ? { ...d, privacy: prev } : d));
      showToast('Could not update privacy');
    }
  }

  if (loading || !detail) {
    return (
      <PremiumPage title="Circle" onBack={() => nav('/circles')}>
        <div className="animate-pulse space-y-3">
          <div className="h-40 rounded-3xl bg-white/5" />
          <div className="h-24 rounded-2xl bg-white/5" />
          <div className="h-48 rounded-2xl bg-white/5" />
        </div>
      </PremiumPage>
    );
  }

  const youId = detail.leaderboard.find((e) => e.isYou)?.userId ?? user?.id;
  const memberNames = Object.fromEntries(detail.members.map((m) => [m.userId, m.name]));

  return (
    <PremiumPage title={detail.name} onBack={() => nav('/circles')}>
      {toast ? (
        <div className="fixed left-1/2 top-20 z-[60] -translate-x-1/2 rounded-full bg-emerald-500/90 px-4 py-2 text-sm font-semibold text-white shadow-lg">
          {toast}
        </div>
      ) : null}

      <PremiumCard variant="hero" className="!p-5">
        <div className="flex items-center gap-4">
          <CircleProgressRing pct={detail.circleProgressPct} />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold uppercase tracking-widest text-sky-300">
              {isToday ? 'Daily momentum' : `Momentum · ${dateLabel}`}
            </div>
            <p className="mt-1 text-sm text-zinc-300">
              {detail.description || 'Circle progress is based on personal goal execution — not raw fitness level.'}
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="rounded-full bg-white/10 px-2.5 py-1 text-zinc-300">{detail.members.length} members</span>
              <span className="rounded-full bg-white/10 px-2.5 py-1 font-mono text-zinc-400">Code: {detail.inviteCode}</span>
            </div>
            <button
              type="button"
              onClick={() => void handleInviteFriends()}
              className="btn-press mt-3 text-left text-sm font-semibold text-violet-300 underline-offset-2 hover:underline"
            >
              Click here to invite your friends
            </button>
          </div>
        </div>
      </PremiumCard>

      <div className="space-y-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setSelectedDateKey(addDaysToDateKey(selectedDateKey, -1))}
            className={`${btnSecondary} px-3 py-2 text-xs`}
          >
            ← Prev
          </button>
          <button
            type="button"
            onClick={() => setSelectedDateKey(addDaysToDateKey(selectedDateKey, 1))}
            disabled={selectedDateKey >= todayKey}
            className={`${btnSecondary} px-3 py-2 text-xs disabled:opacity-40`}
          >
            Next →
          </button>
          <input
            type="date"
            value={selectedDateKey}
            max={todayKey}
            onChange={(e) => {
              if (e.target.value) setSelectedDateKey(e.target.value);
            }}
            className={`${fieldControl} !w-auto !min-h-[40px] !py-2 text-xs`}
          />
          {!isToday ? (
            <button
              type="button"
              onClick={() => setSelectedDateKey(todayKey)}
              className={`${btnToolbarActive} px-3 py-2 text-xs`}
            >
              Back to today
            </button>
          ) : null}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 nice-scroll [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {dayTabs.map((key) => {
            const active = key === selectedDateKey;
            const short =
              key === todayKey
                ? 'Today'
                : key === addDaysToDateKey(todayKey, -1)
                  ? 'Yesterday'
                  : new Date(`${key}T12:00:00`).toLocaleDateString(undefined, {
                      weekday: 'short',
                      day: 'numeric',
                    });
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelectedDateKey(key)}
                className={`shrink-0 rounded-full px-3.5 py-2 text-xs font-semibold transition ${
                  active
                    ? 'bg-sky-500/25 text-sky-100 ring-1 ring-sky-400/40'
                    : 'bg-white/[0.06] text-zinc-400 ring-1 ring-white/10 hover:text-zinc-200'
                }`}
              >
                {short}
              </button>
            );
          })}
        </div>
      </div>

      {isToday ? (
      <div>
        <p className="mb-3 px-1 text-[11px] font-bold uppercase tracking-widest text-[var(--text-muted)]">Quick actions</p>
        <div className="flex gap-2 overflow-x-auto pb-1 nice-scroll">
          <QuickActionButton emoji="🏋️" label="Invite workout" onClick={() => { setPingMessage(''); setPingOpen(true); }} />
          <QuickActionButton emoji="💬" label="Nudge friend" onClick={() => setNudgeOpen(true)} />
          <QuickActionButton emoji="🍽️" label="Share meal" onClick={() => setMealOpen(true)} />
          <QuickActionButton emoji="🏆" label="Start challenge" onClick={() => setChallengeOpen(true)} />
          <QuickActionButton emoji="🔒" label="Privacy" onClick={() => setPrivacyOpen(true)} />
        </div>
      </div>
      ) : null}

      <SectionHeader title={isToday ? 'Leaderboard' : `Leaderboard · ${dateLabel}`} />
      <p className="mb-2 text-xs text-zinc-500">
        Only members who opted into leaderboards (Profile) appear with a score.
      </p>
      <p className="-mt-2 mb-3 px-1 text-xs text-[var(--text-muted)]">
        {isToday
          ? 'Momentum score — who sticks to their plan best, not who lifts the heaviest'
          : `Scores and activity for ${dateLabel}`}
      </p>
      <div className="space-y-2">
        {detail.leaderboard.map((e, i) => (
          <LeaderboardRow
            key={e.userId}
            entry={e}
            delay={i * 0.04}
            onClick={() => void openMember(e.userId)}
          />
        ))}
      </div>

      {isToday && detail.workoutPings.length > 0 ? (
        <>
          <SectionHeader title="Active invites" className="mt-6" />
          <div className="space-y-3">
            {detail.workoutPings.map((p) => (
              <WorkoutPingCard
                key={p.id}
                ping={p}
                currentUserId={user?.id}
                memberNames={memberNames}
                onRespond={(r) => void respondToPing(circleId, p.id, r).then(() => load())}
              />
            ))}
          </div>
        </>
      ) : null}

      {detail.sharedMeals.length > 0 ? (
        <>
          <SectionHeader title="Shared meals" className="mt-6" />
          <div className="space-y-3">
            {detail.sharedMeals.map((m) => (
              <SharedMealCard
                key={m.id}
                meal={m}
                onSave={() => showToast('Recipe saved')}
                onLog={() => nav('/nutrition')}
                onAiAdjust={() => setAiMealOpen(true)}
              />
            ))}
          </div>
        </>
      ) : null}

      {detail.challenges.length > 0 ? (
        <>
          <SectionHeader title="Active challenges" className="mt-6" />
          <div className="space-y-3">
            {detail.challenges.map((c) => (
              <ChallengeCard
                key={c.id}
                challenge={c}
                memberCount={detail.members.length}
                onJoin={() => void joinChallenge(circleId, c.id).then(() => showToast('Joined challenge'))}
              />
            ))}
          </div>
        </>
      ) : null}

      <SectionHeader title={isToday ? 'Activity' : `Activity · ${dateLabel}`} className="mt-6" />
      <div className="space-y-2">
        {detail.activities.length === 0 ? (
          <p className="px-1 text-sm text-[var(--text-muted)]">No circle activity on this day.</p>
        ) : (
          detail.activities.map((a) => <ActivityFeedItem key={a.id} item={a} />)
        )}
      </div>

      <SectionHeader title="Members" className="mt-6" />
      <PremiumCard className="!p-0 !overflow-hidden">
        <ul className="divide-y divide-white/6">
          {detail.members.map((m) => (
            <li key={m.userId}>
              <button
                type="button"
                onClick={() => void openMember(m.userId)}
                className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-white/[0.03]"
              >
                <div>
                  <div className="font-medium text-[var(--text-primary)]">{m.name}</div>
                  <div className="text-xs capitalize text-[var(--text-muted)]">{m.role}</div>
                </div>
                {m.dailyScore != null ? (
                  <span className="text-lg font-bold tabular-nums text-sky-300">{m.dailyScore}</span>
                ) : (
                  <span className="text-xs text-zinc-500">Private</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </PremiumCard>

      {detail.badges.length > 0 ? (
        <>
          <SectionHeader title="Recent badges" className="mt-6" />
          <div className="flex flex-wrap gap-2">
            {detail.badges.map((b) => (
              <span
                key={b.id}
                className="rounded-full border border-amber-500/25 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-200"
                title={b.description}
              >
                {b.title}
              </span>
            ))}
          </div>
        </>
      ) : null}

      {/* Workout ping sheet */}
      <BottomSheet open={pingOpen} onClose={() => setPingOpen(false)} title="Invite workout">
        <div className="grid gap-3">
          <p className="text-sm text-[var(--text-muted)]">Ping the circle with your own workout invite.</p>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-zinc-400">Message</span>
            <input
              value={pingMessage}
              onChange={(e) => setPingMessage(e.target.value)}
              placeholder="Legs at 6:30? Push day after work?"
              className={fieldControl}
            />
          </label>
          <PrimaryButton fullWidth disabled={busy || !pingMessage.trim()} onClick={() => void handlePing()}>
            Send invite
          </PrimaryButton>
        </div>
      </BottomSheet>

      {/* Nudge sheet */}
      <BottomSheet open={nudgeOpen} onClose={() => setNudgeOpen(false)} title="Send a nudge">
        <div className="grid gap-3">
          <p className="text-sm text-[var(--text-muted)]">Always encouraging — never shame-based.</p>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-zinc-400">Send to</span>
            <select value={nudgeTarget} onChange={(e) => setNudgeTarget(e.target.value)} className={fieldControl}>
              <option value="">Choose a friend</option>
              {detail.members.filter((m) => m.userId !== youId).map((m) => (
                <option key={m.userId} value={m.userId}>{m.name}</option>
              ))}
            </select>
          </label>
          <div className="flex flex-wrap gap-2">
            {NUDGE_TEMPLATES.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => { setNudgeType(t.key); setCustomNudge(''); }}
                className={`rounded-xl px-3 py-2 text-xs font-semibold ring-1 ${
                  nudgeType === t.key ? 'bg-violet-500/25 text-violet-100 ring-violet-500/40' : 'bg-white/8 text-zinc-300 ring-white/10'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <textarea
            value={customNudge}
            onChange={(e) => setCustomNudge(e.target.value)}
            placeholder="Or write a custom encouraging message…"
            rows={3}
            className={`${fieldControl} !h-auto resize-y`}
          />
          <PrimaryButton fullWidth disabled={busy || !nudgeTarget} onClick={() => void handleNudge()}>
            Send nudge
          </PrimaryButton>
        </div>
      </BottomSheet>

      {/* Share meal sheet */}
      <BottomSheet open={mealOpen} onClose={() => setMealOpen(false)} title="Share meal">
        <div className="grid gap-3">
          <input value={mealTitle} onChange={(e) => setMealTitle(e.target.value)} placeholder="Meal title" className={fieldControl} />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <label className="block space-y-1"><span className="text-xs text-zinc-400">Calories</span><input inputMode="numeric" value={mealCal} onChange={(e) => setMealCal(e.target.value)} className={fieldControl} /></label>
            <label className="block space-y-1"><span className="text-xs text-zinc-400">Protein</span><input inputMode="decimal" value={mealP} onChange={(e) => setMealP(e.target.value)} className={fieldControl} /></label>
            <label className="block space-y-1"><span className="text-xs text-zinc-400">Carbs</span><input inputMode="decimal" value={mealC} onChange={(e) => setMealC(e.target.value)} className={fieldControl} /></label>
            <label className="block space-y-1"><span className="text-xs text-zinc-400">Fat</span><input inputMode="decimal" value={mealF} onChange={(e) => setMealF(e.target.value)} className={fieldControl} /></label>
          </div>
          <input value={mealPrep} onChange={(e) => setMealPrep(e.target.value)} placeholder="Prep time (minutes)" className={fieldControl} />
          <textarea value={mealRecipe} onChange={(e) => setMealRecipe(e.target.value)} placeholder="Recipe / instructions" rows={3} className={`${fieldControl} !h-auto resize-y`} />
          <PrimaryButton fullWidth disabled={busy || !mealTitle.trim()} onClick={() => void handleShareMeal()}>
            Share with circle
          </PrimaryButton>
        </div>
      </BottomSheet>

      {/* Challenge sheet */}
      <BottomSheet open={challengeOpen} onClose={() => setChallengeOpen(false)} title="Start challenge">
        <div className="grid gap-2">
          {CHALLENGE_TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={busy}
              onClick={() => void handleStartChallenge(t)}
              className="btn-press rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-left ring-1 ring-white/5"
            >
              <div className="font-semibold text-[var(--text-primary)]">{t.title}</div>
              <div className="text-xs text-[var(--text-muted)]">{t.days} days · {t.type}</div>
            </button>
          ))}
        </div>
      </BottomSheet>

      {/* Privacy sheet */}
      <BottomSheet open={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy">
        <p className="mb-4 text-sm text-[var(--text-muted)]">
          Control what this circle can see. Defaults keep sensitive data private.
        </p>
        <div className="space-y-2">
          {(Object.keys(detail.privacy) as (keyof CirclePrivacySettings)[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => void patchPrivacy(key, !detail.privacy[key])}
              className="flex w-full items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left"
            >
              <span className="text-sm text-[var(--text-primary)]">
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
              </span>
              <span
                className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
                  detail.privacy[key] ? 'bg-sky-500/80' : 'bg-zinc-700'
                }`}
              >
                <span
                  className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${
                    detail.privacy[key] ? 'left-[22px]' : 'left-0.5'
                  }`}
                />
              </span>
            </button>
          ))}
        </div>
      </BottomSheet>

      {/* AI meal adjust sheet */}
      <BottomSheet open={aiMealOpen} onClose={() => setAiMealOpen(false)} title="AI macro adjustment">
        <p className="mb-3 text-sm text-[var(--text-muted)]">Ask Coach to adapt a shared recipe to your goals.</p>
        <div className="grid gap-2">
          {AI_MEAL_ADJUSTMENTS.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => { setAiMealOpen(false); nav('/coach'); }}
              className="btn-press rounded-xl border border-violet-500/25 bg-violet-500/10 px-4 py-3 text-left text-sm font-semibold text-violet-100"
            >
              {opt}
            </button>
          ))}
        </div>
      </BottomSheet>

      <MemberDaySheet
        open={memberSheetOpen}
        onClose={() => setMemberSheetOpen(false)}
        snapshot={memberSnapshot}
        loading={memberLoading}
        dateLabel={dateLabel}
      />
    </PremiumPage>
  );
}
